import type { AgeGate } from '@iheartjane/data-access';
import { Button, EditIcon, Flex, Tag, Typography } from '@iheartjane/reefer';
import type { Nullish } from '@iheartjane/types';
import { useCallback, useMemo, useState } from 'react';
import type { UseFormSetValue } from 'react-hook-form';

import type { BloomStore } from '@jane/business-admin/types';
import {
  SUBMIT_BUTTON_VARIANTS,
  StoreSelectModal,
} from '@jane/shared-b2b/components';

import { AgeGateFormContainer } from './AgeGateFormContainer';

import styles from './ageGateStoreSection.module.css';

type SelectedTabType = 'yes_no' | 'date_picker';

const StoreItem = ({ store }: { store: BloomStore }) => {
  const storeAddress = `${store.address}, ${store.city}, ${store.state} ${store.zip}`;

  return (
    <Flex className={styles['storeItem']} alignItems="center" gap={16}>
      <Tag
        background={store.recreational ? 'primary-light' : 'emerald-light'}
        color={store.recreational ? 'primary' : 'emerald'}
        label={store.recreational ? 'Rec' : 'Med'}
      />
      <Flex flexDirection="column">
        <Typography variant="body-bold">{store.name}</Typography>
        <Typography color="grays-mid">{storeAddress}</Typography>
      </Flex>
    </Flex>
  );
};

export const AgeGateStoreSection = ({
  stores,
  selectedTab,
  setValue,
  currentDatePickerStores,
  currentYesNoStores,
}: {
  currentDatePickerStores?: Nullish<number[]>;
  currentYesNoStores?: Nullish<number[]>;
  selectedTab: SelectedTabType;
  setValue: UseFormSetValue<{
    age_gate: AgeGate;
  }>;
  stores: BloomStore[];
}) => {
  const sortedStores = useMemo(() => {
    return [...stores].sort((storeA, storeB) => {
      if (storeA.recreational && !storeB.recreational) return -1;
      if (!storeA.recreational && storeB.recreational) return 1;
      return 0;
    });
  }, [stores]);

  const selectedStores = useMemo(() => {
    return [...sortedStores].filter((store) => {
      if (selectedTab === 'yes_no') {
        return currentYesNoStores?.includes(store.id);
      } else {
        return currentDatePickerStores?.includes(store.id);
      }
    });
  }, [sortedStores, selectedTab, currentDatePickerStores, currentYesNoStores]);

  const [open, setOpen] = useState(false);
  const [datePickerStores, setDatePickerStores] = useState<Set<number>>(
    new Set(currentDatePickerStores ?? [])
  );
  const [yesNoStores, setYesNoStores] = useState<Set<number>>(
    new Set(currentYesNoStores ?? [])
  );

  const selectedStoreIds = useMemo(() => {
    const storeSet = selectedTab === 'yes_no' ? yesNoStores : datePickerStores;
    return Array.from(storeSet).map(String);
  }, [selectedTab, yesNoStores, datePickerStores]);

  // When we are on the yes_no tab, we want to add the store to the yes_no set and remove it from the date_picker set
  // When we are on the date_picker tab, we want to add the store to the date_picker set and remove it from the yes_no set
  const handleToggleStore = useCallback(
    (storeId: string, selected: boolean) => {
      const numericId = Number(storeId);

      if (selectedTab === 'yes_no') {
        if (selected) {
          setYesNoStores((prev) => new Set(prev).add(numericId));
          setDatePickerStores((prev) => {
            const newSet = new Set(prev);
            newSet.delete(numericId);
            return newSet;
          });
        } else {
          setYesNoStores((prev) => {
            const newSet = new Set(prev);
            newSet.delete(numericId);
            return newSet;
          });
          setDatePickerStores((prev) => new Set(prev).add(numericId));
        }
      } else {
        if (selected) {
          setDatePickerStores((prev) => new Set(prev).add(numericId));
          setYesNoStores((prev) => {
            const newSet = new Set(prev);
            newSet.delete(numericId);
            return newSet;
          });
        } else {
          setDatePickerStores((prev) => {
            const newSet = new Set(prev);
            newSet.delete(numericId);
            return newSet;
          });
          setYesNoStores((prev) => new Set(prev).add(numericId));
        }
      }
    },
    [selectedTab]
  );

  const handleToggleAll = useCallback(
    (selected: boolean) => {
      const allStoreIds = new Set(sortedStores.map((store) => store.id));

      if (selectedTab === 'yes_no') {
        if (selected) {
          setYesNoStores(allStoreIds);
          setDatePickerStores(new Set());
        } else {
          setYesNoStores(new Set());
          setDatePickerStores(allStoreIds);
        }
      } else {
        if (selected) {
          setDatePickerStores(allStoreIds);
          setYesNoStores(new Set());
        } else {
          setDatePickerStores(new Set());
          setYesNoStores(allStoreIds);
        }
      }
    },
    [selectedTab, sortedStores]
  );

  const handleSubmit = useCallback(() => {
    setValue('age_gate.yes_no.store_ids', Array.from(yesNoStores), {
      shouldDirty: true,
    });
    setValue('age_gate.date_picker.store_ids', Array.from(datePickerStores), {
      shouldDirty: true,
    });
    setOpen(false);
  }, [setValue, yesNoStores, datePickerStores]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setDatePickerStores(new Set(currentDatePickerStores ?? []));
    setYesNoStores(new Set(currentYesNoStores ?? []));
  }, [currentDatePickerStores, currentYesNoStores]);

  return (
    <AgeGateFormContainer>
      <Flex justifyContent="space-between" mb={16}>
        <Typography variant="header-bold">Stores</Typography>
        <Button.Icon
          label="Edit"
          variant="secondary"
          icon={<EditIcon />}
          onClick={() => setOpen(true)}
        />
      </Flex>
      {selectedStores.map((store) => (
        <StoreItem key={store.id} store={store} />
      ))}
      {open && (
        <StoreSelectModal
          closeModal={handleClose}
          storesData={sortedStores}
          selectedStoreIds={selectedStoreIds}
          onSubmit={handleSubmit}
          onToggleStore={handleToggleStore}
          onToggleAll={handleToggleAll}
          isFetchingStores={false}
          isLoadingStores={false}
          submitButtonType={SUBMIT_BUTTON_VARIANTS.save}
        />
      )}
    </AgeGateFormContainer>
  );
};
